/**
 * Super Admin - Call Center Management
 * Import leads via Excel/CSV; view import history and leads list (read-only).
 * Call center agents cannot add or delete leads – only Super Admin imports.
 */

import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { callCenterAPI } from "utils/api";

const LEAD_STATUSES = [
  "NEW",
  "IN_PROGRESS",
  "CONTACTED",
  "QUALIFIED",
  "CALLBACK_SCHEDULED",
  "NO_ANSWER",
  "WRONG_NUMBER",
  "NOT_INTERESTED",
  "CONVERTED",
];

function SuperAdminCallCenter() {
  const [tab, setTab] = useState(0);
  const [imports, setImports] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadTotal, setLeadTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [file, setFile] = useState(null);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadStatusFilter, setLeadStatusFilter] = useState("");
  const [globalAnalytics, setGlobalAnalytics] = useState(null);

  const loadImports = async () => {
    setLoading(true);
    setMessage((m) => (m.type === "error" ? { type: "", text: "" } : m));
    try {
      const res = await callCenterAPI.getLeadImports();
      setImports(Array.isArray(res?.imports) ? res.imports : []);
    } catch (e) {
      setMessage({ type: "error", text: e?.message || "Failed to load imports" });
      setImports([]);
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    setLoading(true);
    setMessage((m) => (m.type === "error" ? { type: "", text: "" } : m));
    try {
      const params = { limit: 100 };
      if (leadSearch.trim()) params.search = leadSearch.trim();
      if (leadStatusFilter) params.status = leadStatusFilter;
      const res = await callCenterAPI.getLeads(params);
      const list = Array.isArray(res?.leads) ? res.leads : [];
      setLeads(list);
      setLeadTotal(res?.total ?? list.length);
    } catch (e) {
      setMessage({ type: "error", text: e?.message || "Failed to load leads" });
      setLeads([]);
      setLeadTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 0) {
      callCenterAPI
        .getAnalyticsGlobal()
        .then((r) => {
          setGlobalAnalytics(r?.analytics ?? null);
          setMessage((m) =>
            m.type === "error" && m.text?.includes("analytics") ? { type: "", text: "" } : m
          );
        })
        .catch((err) => {
          setGlobalAnalytics(null);
          setMessage({ type: "error", text: err?.message || "Failed to load global analytics" });
        });
    }
    if (tab === 2) loadImports();
    if (tab === 3) loadLeads();
  }, [tab]);

  useEffect(() => {
    if (tab === 3) loadLeads();
  }, [leadSearch, leadStatusFilter]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setMessage({ type: "", text: "" });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage({ type: "error", text: "Select an Excel (.xlsx, .xls) or CSV file" });
      return;
    }
    setUploading(true);
    setMessage({ type: "", text: "" });
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await callCenterAPI.importLeadsExcel(formData);
      setMessage({
        type: "success",
        text: `Import done: ${res.import?.successCount ?? 0} created, ${
          res.import?.duplicateCount ?? 0
        } duplicates, ${res.import?.errorCount ?? 0} errors.`,
      });
      setFile(null);
      if (document.getElementById("excel-upload"))
        document.getElementById("excel-upload").value = "";
      loadImports();
      if (tab === 1) loadLeads();
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Import failed" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={2}>
          <MDTypography variant="h4" fontWeight="medium">
            Call Center Management
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Import leads via Excel or CSV (columns: name, email, phone, source). Call center agents
            view and work these leads; they cannot add or delete records.
          </MDTypography>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type}
            onClose={() => setMessage({ type: "", text: "" })}
            sx={{ mb: 2 }}
          >
            {message.text}
          </Alert>
        )}

        <Card>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
          >
            <Tab label="Global analytics" />
            <Tab label="Import leads" />
            <Tab label="Import history" />
            <Tab label="Leads" />
          </Tabs>

          <MDBox p={3}>
            {tab === 0 && (
              <MDBox>
                <MDTypography variant="h6" gutterBottom>
                  Global Call Center Analytics
                </MDTypography>
                {globalAnalytics && typeof globalAnalytics === "object" ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ p: 2 }}>
                        <MDTypography variant="caption" color="text.secondary">
                          Total leads
                        </MDTypography>
                        <MDTypography variant="h4">{globalAnalytics.totalLeads ?? 0}</MDTypography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ p: 2 }}>
                        <MDTypography variant="caption" color="text.secondary">
                          Converted
                        </MDTypography>
                        <MDTypography variant="h4">
                          {globalAnalytics.convertedLeads ?? 0}
                        </MDTypography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ p: 2 }}>
                        <MDTypography variant="caption" color="text.secondary">
                          Conversion rate %
                        </MDTypography>
                        <MDTypography variant="h4">
                          {globalAnalytics.conversionRate ?? 0}%
                        </MDTypography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ p: 2 }}>
                        <MDTypography variant="caption" color="text.secondary">
                          Total calls
                        </MDTypography>
                        <MDTypography variant="h4">{globalAnalytics.totalCalls ?? 0}</MDTypography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ p: 2 }}>
                        <MDTypography variant="caption" color="text.secondary">
                          Stuck properties
                        </MDTypography>
                        <MDTypography variant="h4">{globalAnalytics.stuckCount ?? 0}</MDTypography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ p: 2 }}>
                        <MDTypography variant="caption" color="text.secondary">
                          Task completion %
                        </MDTypography>
                        <MDTypography variant="h4">
                          {globalAnalytics.taskCompletionRate ?? 0}%
                        </MDTypography>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <MDTypography variant="subtitle2" gutterBottom>
                        Lead funnel by status
                      </MDTypography>
                      <MDBox
                        component="pre"
                        sx={{ bgcolor: "grey.100", p: 1, borderRadius: 1, overflow: "auto" }}
                      >
                        {JSON.stringify(globalAnalytics.funnelByStatus || {}, null, 2)}
                      </MDBox>
                    </Grid>
                    <Grid item xs={12}>
                      <MDTypography variant="subtitle2" gutterBottom>
                        Import stats
                      </MDTypography>
                      <MDBox
                        component="pre"
                        sx={{ bgcolor: "grey.100", p: 1, borderRadius: 1, overflow: "auto" }}
                      >
                        {JSON.stringify(globalAnalytics.importStats || {}, null, 2)}
                      </MDBox>
                    </Grid>
                  </Grid>
                ) : (
                  <MDBox py={2}>
                    {message.text && message.type === "error" ? (
                      <Alert severity="error">{message.text}</Alert>
                    ) : (
                      <CircularProgress />
                    )}
                  </MDBox>
                )}
              </MDBox>
            )}

            {tab === 1 && (
              <MDBox>
                <MDTypography variant="h6" gutterBottom>
                  Upload Excel or CSV
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={2}>
                  First row: headers (name, email, phone, source). Supported: .xlsx, .xls, .csv
                </MDTypography>
                <form onSubmit={handleUpload}>
                  <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                    <Button variant="outlined" component="label" disabled={uploading}>
                      Choose file
                      <input
                        id="excel-upload"
                        type="file"
                        hidden
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {file && <MDTypography variant="body2">{file.name}</MDTypography>}
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="primary"
                      disabled={!file || uploading}
                    >
                      {uploading ? <CircularProgress size={24} color="inherit" /> : "Import"}
                    </MDButton>
                  </Box>
                </form>
              </MDBox>
            )}

            {tab === 2 && (
              <>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>File</TableCell>
                          <TableCell>Rows</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Duplicates</TableCell>
                          <TableCell>Errors</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {imports.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No imports yet
                            </TableCell>
                          </TableRow>
                        ) : (
                          imports.map((imp) => (
                            <TableRow key={imp._id}>
                              <TableCell>
                                {imp.createdAt ? new Date(imp.createdAt).toLocaleString() : "—"}
                              </TableCell>
                              <TableCell>{imp.fileName || "—"}</TableCell>
                              <TableCell>{imp.rowCount ?? 0}</TableCell>
                              <TableCell>{imp.successCount ?? 0}</TableCell>
                              <TableCell>{imp.duplicateCount ?? 0}</TableCell>
                              <TableCell>{imp.errorCount ?? 0}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={imp.status}
                                  color={
                                    imp.status === "COMPLETED"
                                      ? "success"
                                      : imp.status === "FAILED"
                                      ? "error"
                                      : "default"
                                  }
                                />
                              </TableCell>
                              <TableCell>{imp.importedBy?.fullName || "—"}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {tab === 3 && (
              <>
                <MDBox display="flex" flexWrap="wrap" gap={2} mb={2}>
                  <TextField
                    size="small"
                    placeholder="Search name, email, phone"
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={leadStatusFilter}
                      label="Status"
                      onChange={(e) => setLeadStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {LEAD_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{ alignSelf: "center" }}
                  >
                    Total: {leadTotal}
                  </MDTypography>
                </MDBox>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Assigned</TableCell>
                          <TableCell>Source</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leads.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              No leads
                            </TableCell>
                          </TableRow>
                        ) : (
                          leads.map((lead) => (
                            <TableRow key={lead._id}>
                              <TableCell>{lead.name}</TableCell>
                              <TableCell>{lead.email}</TableCell>
                              <TableCell>{lead.phone}</TableCell>
                              <TableCell>
                                <Chip size="small" label={lead.status} />
                              </TableCell>
                              <TableCell>{lead.assignedTo?.fullName || "—"}</TableCell>
                              <TableCell>{lead.source || "—"}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SuperAdminCallCenter;
